import useIsMountedRef from "Common/componentUtils/useIsMountedRef";
import { formatSize, getBase64, getExtensionFileFromName } from "Common/fileUtils/fileUtils";
import { ImagePreviewModal } from "Components/previewModal";
import ProgressOverlay from "Components/progressOverlay/progressOverlay";
import classNames from "classnames";
import { FileUploader } from "devextreme-react";
import includes from "lodash/includes";
import isString from "lodash/isString";
import join from "lodash/join";
import map from "lodash/map";
import toUpper from "lodash/toUpper";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE } from "./constants";
import locale from "./imageWidget.locale";
import Styled from "./imageWidget.styled";
import styles from "./imageWidget.module.css";
import { Button } from "UiKit";
const invalidMessagePosition = {
    my: "top left",
    at: "bottom left",
    collision: "none"
};
function ImageWidget(props) {
    const isMountedRef = useIsMountedRef();
    const [sizeError, setSizeError] = useState(false);
    const [extensionError, setExtensionError] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewValue, setPreviewValue] = useState("");
    const [loadPreviewInProgress, setLoadPreviewInProgress] = useState(false);
    const editId = useMemo(() => `editId${uuid()}`, []);
    const containerId = useMemo(() => `containerFor_${editId}`, [editId]);
    const size = isString(props.size) ? props.size : `${props.size}px`;
    useEffect(() => {
        setSizeError(false);
        setExtensionError(false);
        setShowPreview(false);
        setPreviewValue("");
        setLoadPreviewInProgress(false);
    }, [props.value]);
    const handleDelete = useCallback(() => {
        setSizeError(false);
        setExtensionError(false);
        setShowPreview(false);
        setPreviewValue("");
        setLoadPreviewInProgress(false);
        props.onChange && props.onChange(undefined, props.fieldName);
    }, [props.onChange, props.fieldName]);
    const handleShowPreview = useCallback(() => {
        if (!props.value || !props.onGetPreview)
            return;
        const defaultValue = props.value;
        setLoadPreviewInProgress(true);
        props.onGetPreview(async (base64) => {
            if (!isMountedRef.current)
                return;
            setPreviewValue(base64 || defaultValue);
            setLoadPreviewInProgress(false);
        }, () => {
            if (!isMountedRef.current)
                return;
            setPreviewValue(defaultValue);
            setLoadPreviewInProgress(false);
        });
        setShowPreview(true);
    }, [props.value]);
    const handleValueChanged = useCallback(async ({ value }) => {
        const sizeInvalid = Boolean((value === null || value === void 0 ? void 0 : value.length) && value[0].size > MAX_FILE_SIZE);
        const extensionInvalid = Boolean((value === null || value === void 0 ? void 0 : value.length) && !includes(ALLOWED_EXTENSIONS, getExtensionFileFromName(value[0].name)));
        setSizeError(sizeInvalid);
        setExtensionError(extensionInvalid);
        if (sizeInvalid || extensionInvalid)
            return;
        if ((value === null || value === void 0 ? void 0 : value.length) === 1) {
            const result = await getBase64(value[0]);
            result && props.onChange && props.onChange(result.toString(), props.fieldName);
        }
    }, [props.onChange, props.fieldName]);
    const uploadRestrictionsDescription = useMemo(() => locale.uploadText(formatSize(MAX_FILE_SIZE), join(map(ALLOWED_EXTENSIONS, toUpper), "/")), []);
    const errorContent = useMemo(() => sizeError || extensionError ? (React.createElement(React.Fragment, null,
        sizeError && (React.createElement("span", { className: "text-danger-400" }, locale.sizeMessage(formatSize(MAX_FILE_SIZE)))),
        extensionError && (React.createElement("span", { className: "text-danger-400" }, locale.fileExtensionMessage(join(map(ALLOWED_EXTENSIONS, toUpper), "/")))))) : undefined, [sizeError, extensionError]);
    const handleHidePreview = useCallback(() => {
        setShowPreview(false);
    }, []);
    const img = useMemo(() => (React.createElement("img", { className: "border-rounded-circle bg-gray-050 img-cover", width: "100%", height: "100%", src: props.value || props.defaultValue || "", title: "", alt: "" })), [props.value, props.defaultValue]);
    const hasActions = (props.onGetPreview && props.value) || !props.readOnly;
    const invalidMessageVisibleProps = useMemo(() => (props.invalidMessage ? { defaultVisible: true, visible: true } : {}), [props.invalidMessage]);
    return (React.createElement("div", { id: containerId, className: classNames(props.className, loadPreviewInProgress && "no-pointer-events") },
        React.createElement("div", { className: "d-flex", style: { alignItems: "center", gap: 9 } },
            React.createElement(Styled.ImageContainer, { size: parseInt(size), error: Boolean(errorContent || props.invalidMessage).toString(), className: classNames(!hasActions && "no-pointer-events"), popoverContent: errorContent || props.invalidMessage || "", popoverClassName: "tooltip-invalid-message", showEvent: props.invalidMessage ? "load" : "dxhoverstart", hideEvent: props.invalidMessage ? "unload" : "dxhoverend", position: invalidMessagePosition, container: `#${containerId}`, ...invalidMessageVisibleProps },
                React.createElement("div", null, props.onGetPreview && props.value && (React.createElement(Styled.PreviewButton, { size: parseInt(size), popoverContent: locale.SHOW_PREVIEW, popoverClassName: "dark-popover", showEvent: "dxhoverstart", hideEvent: "dxhoverend", zIndex: props.zIndex },
                    React.createElement("i", { className: "icon-glass-plus clickable font-h5 font-bold border-rounded-circle", onClick: handleShowPreview })))),
                props.onGetPreview ? (React.createElement(ProgressOverlay, { show: loadPreviewInProgress }, img)) : (img)),
            !props.readOnly && (React.createElement("div", { className: styles.actions },
                React.createElement(Button, { id: editId, size: "36", styleType: "tonal", tooltipMessage: !errorContent ? uploadRestrictionsDescription : undefined, className: styles.btn },
                    React.createElement("i", { className: "icon-upload font-bold" }),
                    React.createElement("span", null, props.value ? locale.EDIT : locale.ADD)),
                props.value && (React.createElement(Button, { size: "36", styleType: "outlined-danger", onClick: handleDelete, className: styles.btn },
                    React.createElement("span", null, locale.DELETE)))))),
        !props.readOnly && (React.createElement(FileUploader, { className: "d-none", dialogTrigger: `#${editId}`, uploadMode: "instantly", onValueChanged: handleValueChanged, hoverStateEnabled: false, focusStateEnabled: false, disabled: props.readOnly, accept: join(map(ALLOWED_EXTENSIONS, (item) => `image/${item}`), ",") })),
        props.onGetPreview && previewValue && (React.createElement(ImagePreviewModal, { src: previewValue, show: showPreview, onHide: handleHidePreview, title: props.previewTitle }))));
}
export default memo(ImageWidget);
